import { loadGlb, type GltfMeshAsset } from "../assets/GltfLoader";
import {
  findNearestWaterCell,
  sampleTerrainSurface,
  type TerrainAsset,
} from "../assets/TerrainAsset";
import type { Mat4, StaticMeshPass } from "../passes/StaticMeshPass";
import type { Aabb, AabbImpact } from "./Collisions";
import type { DockFootprint } from "./building-extrude";
import {
  FloatingObjectsLibrary,
  type FloatingObjectAxis,
  type FloatingObjectEntry,
} from "./FloatingObjectsLibrary";

const DEFAULT_MAX_OBJECTS = 6;
const DOCK_STANDOFF_M = 1.2;
const SPRING_STIFFNESS = 5.5;
const SPRING_DAMPING = 3.1;
const IMPACT_IMPULSE_GAIN = 0.85;
const PENETRATION_NUDGE_GAIN = 0.35;
const MAX_OFFSET_M = 2.2;
const FALLBACK_PLACEMENTS: Array<{ x: number; z: number; yaw: number }> = [
  { x: 32, z: -20, yaw: 0.2 },
  { x: 44, z: -6, yaw: -0.12 },
  { x: 36, z: 10, yaw: 0.08 },
  { x: 52, z: 22, yaw: -0.18 },
];

interface Placement {
  x: number;
  z: number;
  yaw: number;
}

interface BuildArgs {
  staticMeshPass: StaticMeshPass;
  dockFootprints: readonly DockFootprint[];
  staticAabbs: Aabb[];
  waterLevel: number;
  terrain?: TerrainAsset;
  maxObjects?: number;
}

interface MeshDims {
  length: number;
  beam: number;
}

interface FloatingObjectInstance {
  meshId: string;
  model: Mat4;
  baseTx: number;
  baseTz: number;
  baseAabb: Aabb;
  aabb?: Aabb;
  offsetX: number;
  offsetZ: number;
  velX: number;
  velZ: number;
}

export class FloatingObjectsController {
  constructor(
    private readonly staticMeshPass: StaticMeshPass,
    private readonly instances: FloatingObjectInstance[],
  ) {}

  step(dt: number): void {
    const dirtyMeshIds = new Set<string>();
    for (const obj of this.instances) {
      const ax = -SPRING_STIFFNESS * obj.offsetX - SPRING_DAMPING * obj.velX;
      const az = -SPRING_STIFFNESS * obj.offsetZ - SPRING_DAMPING * obj.velZ;
      obj.velX += ax * dt;
      obj.velZ += az * dt;
      obj.offsetX += obj.velX * dt;
      obj.offsetZ += obj.velZ * dt;
      clampOffset(obj);

      if (
        Math.abs(obj.offsetX) < 0.001 &&
        Math.abs(obj.offsetZ) < 0.001 &&
        Math.hypot(obj.velX, obj.velZ) < 0.01
      ) {
        obj.offsetX = 0;
        obj.offsetZ = 0;
        obj.velX = 0;
        obj.velZ = 0;
      }

      obj.model[12] = obj.baseTx + obj.offsetX;
      obj.model[14] = obj.baseTz + obj.offsetZ;
      if (obj.aabb)
        updateShiftedAabb(obj.aabb, obj.baseAabb, obj.offsetX, obj.offsetZ);
      dirtyMeshIds.add(obj.meshId);
    }
    for (const meshId of dirtyMeshIds) this.staticMeshPass.markDirty(meshId);
  }

  get count(): number {
    return this.instances.length;
  }
}

export async function buildFloatingObjects(
  args: BuildArgs,
): Promise<FloatingObjectsController> {
  const empty = new FloatingObjectsController(args.staticMeshPass, []);
  let library: FloatingObjectsLibrary;
  try {
    library = await FloatingObjectsLibrary.load();
  } catch (err) {
    console.warn("[FloatingObjects] library load failed, skipping", err);
    return empty;
  }

  const entries = library.list();
  if (entries.length === 0) return empty;

  let placedTotal = 0;
  const instances: FloatingObjectInstance[] = [];
  for (const entry of entries) {
    const placed = await buildEntry({ ...args, entry, instances });
    placedTotal += placed;
  }
  if (placedTotal > 0) {
    console.log(
      `[FloatingObjects] placed ${placedTotal} moored static object(s)`,
    );
  }
  return new FloatingObjectsController(args.staticMeshPass, instances);
}

async function buildEntry(
  args: BuildArgs & {
    entry: FloatingObjectEntry;
    instances: FloatingObjectInstance[];
  },
): Promise<number> {
  const { entry, staticMeshPass, waterLevel, staticAabbs } = args;
  let mesh: GltfMeshAsset;
  try {
    mesh = await loadGlb(entry.url);
  } catch (err) {
    console.warn(`[FloatingObjects] failed to load ${entry.url}`, err);
    return 0;
  }

  const scale = entry.render?.scale ?? 1;
  const dims = meshDims(mesh, entry.render?.axisUp, entry.render?.axisLength);
  const meshId = `floating/${entry.id}`;
  staticMeshPass.addGroup(meshId, mesh, 0, {
    castsShadow: entry.render?.castsShadow ?? true,
    castsHardShadow: true,
    receivesShadow: entry.render?.receivesShadow ?? true,
  });

  const maxObjects = args.maxObjects ?? DEFAULT_MAX_OBJECTS;
  const placements = placementsForDocks(args, dims, scale, maxObjects);
  if (placements.length < maxObjects) {
    placements.push(
      ...fallbackPlacements(
        args.terrain,
        maxObjects - placements.length,
        placements.length,
      ),
    );
  }

  let placed = 0;
  for (const placement of placements.slice(0, maxObjects)) {
    if (
      args.terrain &&
      sampleTerrainSurface(args.terrain, placement.x, placement.z) !== "water"
    ) {
      continue;
    }
    const yaw = placement.yaw + ((entry.render?.yawDeg ?? 0) * Math.PI) / 180;
    const model = transformForMesh(
      mesh,
      scale,
      yaw,
      placement.x,
      waterLevel,
      placement.z,
      entry.render?.yAlign,
      entry.render?.yOffset,
    );
    staticMeshPass.addInstance(meshId, model);
    const instance: FloatingObjectInstance = {
      meshId,
      model,
      baseTx: model[12]!,
      baseTz: model[14]!,
      baseAabb: aabbForMesh(
        mesh,
        scale,
        yaw,
        placement.x,
        waterLevel,
        placement.z,
        entry.render?.yAlign,
        entry.render?.yOffset,
        0,
      ),
      offsetX: 0,
      offsetZ: 0,
      velX: 0,
      velZ: 0,
    };
    if (
      entry.collision.type === "mesh-aabb" &&
      entry.collision.solid !== false
    ) {
      const baseAabb = aabbForMesh(
        mesh,
        scale,
        yaw,
        placement.x,
        waterLevel,
        placement.z,
        entry.render?.yAlign,
        entry.render?.yOffset,
        entry.collision.paddingM ?? 0,
      );
      const aabb: Aabb = {
        min: baseAabb.min,
        max: baseAabb.max,
        onBoatImpact: (impact) => pushFloatingObject(instance, impact),
      };
      instance.baseAabb = baseAabb;
      instance.aabb = aabb;
      staticAabbs.push(aabb);
    }
    args.instances.push(instance);
    placed++;
  }
  return placed;
}

function placementsForDocks(
  args: BuildArgs,
  dims: MeshDims,
  scale: number,
  maxObjects: number,
): Placement[] {
  const placements: Placement[] = [];
  const length = Math.max(1, dims.length * scale);
  for (const footprint of args.dockFootprints) {
    if (placements.length >= maxObjects) break;
    if (footprint.mooringStyle === "dock-only") continue;
    const cosY = Math.cos(footprint.yaw);
    const sinY = Math.sin(footprint.yaw);
    const slots = Math.max(1, Math.min(3, Math.floor(footprint.sx / 10)));
    for (let slot = 0; slot < slots && placements.length < maxObjects; slot++) {
      const side = slot % 2 === 0 ? 1 : -1;
      const t = slots === 1 ? 0.5 : (slot + 0.5) / slots;
      const xLocal = -footprint.sx * 0.5 + t * footprint.sx;
      const zEdge = side * (footprint.sz * 0.5 + DOCK_STANDOFF_M);
      const sternX = footprint.x + cosY * xLocal + sinY * zEdge;
      const sternZ = footprint.z - sinY * xLocal + cosY * zEdge;
      const outX = sinY * side;
      const outZ = cosY * side;
      const x = sternX + outX * (length * 0.5 + 0.5);
      const z = sternZ + outZ * (length * 0.5 + 0.5);
      if (
        args.terrain &&
        sampleTerrainSurface(args.terrain, x, z) !== "water"
      ) {
        continue;
      }
      placements.push({ x, z, yaw: Math.atan2(outX, outZ) });
    }
  }
  return placements;
}

function fallbackPlacements(
  terrain: TerrainAsset | undefined,
  count: number,
  offset: number,
): Placement[] {
  const out: Placement[] = [];
  for (let i = 0; i < count; i++) {
    const p = FALLBACK_PLACEMENTS[(i + offset) % FALLBACK_PLACEMENTS.length]!;
    let x = p.x;
    let z = p.z;
    if (terrain) {
      const snapped = findNearestWaterCell(terrain, x, z, 5, 80);
      if (!snapped) continue;
      x = snapped[0];
      z = snapped[1];
    }
    out.push({ x, z, yaw: p.yaw });
  }
  return out;
}

function meshDims(
  mesh: GltfMeshAsset,
  axisUp: FloatingObjectAxis = "Y",
  axisLength: FloatingObjectAxis = "Z",
): MeshDims {
  const ext = extents(mesh);
  const length = ext[axisIndex(axisLength)];
  const horizontal = ["X", "Y", "Z"].filter(
    (axis) => axis !== axisUp && axis !== axisLength,
  ) as FloatingObjectAxis[];
  const beamAxis = horizontal[0] ?? "X";
  return { length, beam: ext[axisIndex(beamAxis)] };
}

function extents(mesh: GltfMeshAsset): [number, number, number] {
  return [
    mesh.bbox.max[0] - mesh.bbox.min[0],
    mesh.bbox.max[1] - mesh.bbox.min[1],
    mesh.bbox.max[2] - mesh.bbox.min[2],
  ];
}

function axisIndex(axis: FloatingObjectAxis): 0 | 1 | 2 {
  return axis === "X" ? 0 : axis === "Y" ? 1 : 2;
}

function transformForMesh(
  mesh: GltfMeshAsset,
  scale: number,
  yaw: number,
  x: number,
  waterLevel: number,
  z: number,
  yAlign: "raw" | "bbox-bottom" = "raw",
  yOffset = 0,
): Mat4 {
  const c = Math.cos(yaw);
  const s = Math.sin(yaw);
  const centerX = (mesh.bbox.min[0] + mesh.bbox.max[0]) * 0.5;
  const centerZ = (mesh.bbox.min[2] + mesh.bbox.max[2]) * 0.5;
  const ox = (c * centerX + s * centerZ) * scale;
  const oz = (-s * centerX + c * centerZ) * scale;
  const tx = x - ox;
  const baseY = yAlign === "bbox-bottom" ? mesh.bbox.min[1] : 0;
  const ty = waterLevel + yOffset - baseY * scale;
  const tz = z - oz;
  // prettier-ignore
  return new Float32Array([
    c * scale, 0, -s * scale, 0,
    0, scale, 0, 0,
    s * scale, 0, c * scale, 0,
    tx, ty, tz, 1,
  ]) as Mat4;
}

function aabbForMesh(
  mesh: GltfMeshAsset,
  scale: number,
  yaw: number,
  x: number,
  waterLevel: number,
  z: number,
  yAlign: "raw" | "bbox-bottom" | undefined,
  yOffset: number | undefined,
  padding: number,
): Aabb {
  const model = transformForMesh(
    mesh,
    scale,
    yaw,
    x,
    waterLevel,
    z,
    yAlign,
    yOffset,
  );
  let minX = Infinity;
  let minY = Infinity;
  let minZ = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  let maxZ = -Infinity;
  for (const lx of [mesh.bbox.min[0], mesh.bbox.max[0]]) {
    for (const ly of [mesh.bbox.min[1], mesh.bbox.max[1]]) {
      for (const lz of [mesh.bbox.min[2], mesh.bbox.max[2]]) {
        const wx =
          model[0]! * lx + model[4]! * ly + model[8]! * lz + model[12]!;
        const wy =
          model[1]! * lx + model[5]! * ly + model[9]! * lz + model[13]!;
        const wz =
          model[2]! * lx + model[6]! * ly + model[10]! * lz + model[14]!;
        minX = Math.min(minX, wx);
        minY = Math.min(minY, wy);
        minZ = Math.min(minZ, wz);
        maxX = Math.max(maxX, wx);
        maxY = Math.max(maxY, wy);
        maxZ = Math.max(maxZ, wz);
      }
    }
  }
  return {
    min: [minX - padding, minY - padding, minZ - padding],
    max: [maxX + padding, maxY + padding, maxZ + padding],
  };
}

function pushFloatingObject(
  obj: FloatingObjectInstance,
  impact: AabbImpact,
): void {
  const awayX = -impact.normalX;
  const awayZ = -impact.normalZ;
  const impulse = Math.min(2.8, impact.speed * IMPACT_IMPULSE_GAIN);
  obj.velX += awayX * impulse;
  obj.velZ += awayZ * impulse;
  obj.offsetX +=
    awayX * Math.min(0.45, impact.penetration * PENETRATION_NUDGE_GAIN);
  obj.offsetZ +=
    awayZ * Math.min(0.45, impact.penetration * PENETRATION_NUDGE_GAIN);
  clampOffset(obj);
}

function clampOffset(obj: FloatingObjectInstance): void {
  const d = Math.hypot(obj.offsetX, obj.offsetZ);
  if (d <= MAX_OFFSET_M) return;
  const s = MAX_OFFSET_M / d;
  obj.offsetX *= s;
  obj.offsetZ *= s;
  obj.velX *= 0.35;
  obj.velZ *= 0.35;
}

function updateShiftedAabb(
  target: Aabb,
  base: Aabb,
  offsetX: number,
  offsetZ: number,
): void {
  target.min = [base.min[0] + offsetX, base.min[1], base.min[2] + offsetZ];
  target.max = [base.max[0] + offsetX, base.max[1], base.max[2] + offsetZ];
}
